
<template>
  <WelcomeHeader />

  <!-- ════════════════════════════════════════════════════════════
       PREMIUM LANDING LAYOUT  (courses with layout === 'premium')
       ════════════════════════════════════════════════════════════ -->
  <div v-if="isPremium" class="pl">

    <!-- ══ HERO (dark) ══ -->
    <section class="pl-hero">
      <div class="pl-hero-glow"></div>
      <div class="pl-container pl-hero-inner">
        <a class="pl-back" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          সকল কোর্স
        </a>
        <div class="pl-hero-badge-row" v-if="courseDetails.hero_badge">
          <span class="pl-hero-badge"><span class="pl-hero-pulse"></span>{{ courseDetails.hero_badge }}</span>
        </div>
        <!-- Compact text hero (no banner) -->
        <template v-if="courseDetails.hero_layout === 'text'">
          <h1 class="pl-hero-title">{{ courseDetails.title }}</h1>
          <p class="pl-hero-sub" v-if="courseDetails.hero_subtitle">{{ courseDetails.hero_subtitle }}</p>
        </template>
        <!-- Banner hero (default) -->
        <template v-else>
          <h1 class="pl-sr-only">{{ courseDetails.title }}</h1>
          <div class="pl-banner-wrap">
            <img class="pl-banner" :src="courseDetails.image" :alt="courseDetails.title" />
          </div>
        </template>
        <div class="pl-hero-cta">
          <a class="pl-btn pl-btn-glow" @click="scrollToEnroll">
            {{ courseDetails.hero_cta || 'এখনই এনরোল করুন' }}
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <a v-if="courseDetails.routine && courseDetails.routine.length"
             class="pl-btn pl-btn-soft" @click="scrollToRoutine">
            <i class="fas fa-calendar-alt"></i> রুটিন দেখুন
          </a>
          <a href="tel:+8801894442944" class="pl-btn pl-btn-line">
            <i class="fas fa-phone-alt"></i> ০১৮৯৪-৪৪২৯৪৪
          </a>
        </div>
      </div>

      <!-- Floating stats strip overlapping hero/feature boundary -->
      <div class="pl-container">
        <div class="pl-stats" v-if="courseDetails.stats && courseDetails.stats.length">
          <div class="pl-stat" v-for="(s, i) in courseDetails.stats" :key="i">
            <span class="pl-stat-ic"><i class="fas" :class="s.icon"></i></span>
            <span class="pl-stat-text">
              <span class="pl-stat-val">{{ s.value }}</span>
              <span class="pl-stat-lbl">{{ s.label }}</span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ FEATURES (light blue) ══ -->
    <section class="pl-features">
      <div class="pl-container">
        <div class="pl-head">
          <span class="pl-eyebrow">কেন এই কোর্সটি সেরা</span>
          <h2 class="pl-h2">{{ courseDetails.features_title || 'এই কোর্সে যা যা থাকছে' }}</h2>
          <p class="pl-note">{{ courseDetails.features_subtitle || 'সম্পূর্ণ সিলেবাস টার্গেট করে সাজানো' }}</p>
        </div>
        <div class="pl-feat-grid">
          <div class="pl-feat-card" v-for="(f, i) in courseDetails.features" :key="i"
               :class="'fc-' + (i % 6)" :style="{ animationDelay: (i * 0.07) + 's' }">
            <span class="pl-feat-ic"><i class="fas" :class="f.icon"></i></span>
            <h3 class="pl-feat-title">{{ f.title }}</h3>
            <p class="pl-feat-desc">{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ WHY (dark indigo band) ══ -->
    <section class="pl-why" v-if="courseDetails.why_title">
      <div class="pl-why-orb pl-why-orb-a"></div>
      <div class="pl-why-orb pl-why-orb-b"></div>
      <div class="pl-container pl-why-inner">
        <span class="pl-why-mark"><i class="fas fa-graduation-cap"></i></span>
        <h2 class="pl-why-title">{{ courseDetails.why_title }}</h2>
        <p class="pl-why-text">{{ courseDetails.why_text }}</p>
      </div>
    </section>

    <!-- ══ ROUTINE (light, colour-coded table) ══ -->
    <section class="pl-routine" id="pl-routine" ref="routineRef" v-if="courseDetails.routine && courseDetails.routine.length">
      <div class="pl-container">
        <div class="pl-head">
          <span class="pl-eyebrow">সম্পূর্ণ রুটিন</span>
          <h2 class="pl-h2">{{ courseDetails.routine_title || '১৬টি পরীক্ষার সম্পূর্ণ রুটিন' }}</h2>
          <p class="pl-note">{{ courseDetails.routine_note || '২২ জুন – ২২ জুলাই • প্রতিটি বিষয় আলাদা রঙে চিহ্নিত' }}</p>
        </div>

        <!-- subject colour legend -->
        <div class="pl-legend">
          <span class="pl-legend-item" v-for="key in legendGroups" :key="key" :class="'rg-' + key">
            <span class="pl-legend-dot"></span>{{ groupMeta[key].name }}
          </span>
        </div>

        <!-- ── MONTH-WISE VIEW (long routines, e.g. 6-month / 144 exams) ── -->
        <template v-if="isMonthRoutine">
          <div class="pl-month-switch">
            <span class="pl-month-label"><i class="fas fa-hand-pointer"></i> মাস বেছে নিন — যেকোনো মাসে ক্লিক করে সেই মাসের রুটিন দেখুন</span>
            <div class="pl-month-tabs" role="tablist">
              <button v-for="mo in routineMonths" :key="mo.key" type="button"
                      class="pl-month-tab" :class="{ active: mo.key === activeMonthKey }"
                      @click="setMonth(mo.key)">
                <span class="pl-month-tab-name">{{ mo.name }}</span>
                <span class="pl-month-tab-count">{{ mo.rows.length }}</span>
              </button>
            </div>
          </div>

          <div class="pl-routine-card">
            <div class="pl-phase" v-for="mo in routineMonths" :key="mo.key" v-show="mo.key === activeMonthKey">
              <div class="pl-phase-head">
                <span class="pl-phase-tag">{{ mo.name }} মাসের রুটিন</span>
                <span class="pl-phase-count">{{ mo.rows.length }} টি পরীক্ষা</span>
              </div>
              <div class="pl-table-wrap">
                <table class="pl-table">
                  <thead>
                    <tr>
                      <th class="c-no">#</th>
                      <th class="c-date">তারিখ</th>
                      <th class="c-day">বার</th>
                      <th class="c-sub">বিষয়</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in mo.rows" :key="r.no" :class="'rg-' + r.group"
                        :style="{ animationDelay: (i * 0.03) + 's' }">
                      <td class="c-no"><span class="pl-rno">{{ r.no }}</span></td>
                      <td class="c-date">{{ r.date }}</td>
                      <td class="c-day">{{ r.day }}</td>
                      <td class="c-sub">
                        <span class="pl-sub">
                          <span class="pl-sub-text">{{ r.subject }}</span>
                          <span class="pl-sub-chip">{{ groupMeta[r.group] ? groupMeta[r.group].name : '' }}</span>
                        </span>
                        <div class="pl-syl" v-if="r.syllabus">
                          <span class="pl-syl-ico">সিলেবাস</span>
                          <ul class="pl-syl-list">
                            <li v-for="(s, si) in syllabusItems(r.syllabus)" :key="si">{{ s }}</li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <!-- ── PHASE VIEW (default, e.g. Primary Headmaster) ── -->
        <div v-else class="pl-routine-card">
          <div class="pl-phase" v-for="phase in routinePhases" :key="phase.phase">
            <div class="pl-phase-head">
              <span class="pl-phase-tag">{{ phase.label }}</span>
              <span class="pl-phase-count">{{ phase.rows.length }} টি পরীক্ষা</span>
            </div>
            <div class="pl-table-wrap">
              <table class="pl-table">
                <thead>
                  <tr>
                    <th class="c-no">#</th>
                    <th class="c-date">তারিখ</th>
                    <th class="c-day">বার</th>
                    <th class="c-sub">বিষয়</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in phase.rows" :key="r.no" :class="'rg-' + r.group"
                      :style="{ animationDelay: (i * 0.04) + 's' }">
                    <td class="c-no"><span class="pl-rno">{{ r.no }}</span></td>
                    <td class="c-date">{{ r.date }}</td>
                    <td class="c-day">{{ r.day }}</td>
                    <td class="c-sub">
                      <span class="pl-sub">
                        <span class="pl-sub-text">{{ r.subject }}</span>
                        <span class="pl-sub-chip">{{ groupMeta[r.group] ? groupMeta[r.group].name : '' }}</span>
                      </span>
                      <div class="pl-syl" v-if="r.syllabus">
                        <span class="pl-syl-ico">সিলেবাস</span>
                        <ul class="pl-syl-list">
                          <li v-for="(s, si) in syllabusItems(r.syllabus)" :key="si">{{ s }}</li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ── Download the printable routine (lead-capture opt-in) ── -->
        <GuidelineOptin
          v-if="courseDetails.routine_pdf"
          subject="primary_headmaster_routine"
          :pdf="courseDetails.routine_pdf"
          heading="সম্পূর্ণ রুটিনের প্রিন্টযোগ্য <span class=&quot;accent&quot;>PDF</span> ফ্রি নিন"
          sub-text="মোবাইল নম্বর দিন — সাথে সাথেই ১৬টি পরীক্ষার সম্পূর্ণ রুটিন ডাউনলোড করুন।"
          download-label="রুটিন ডাউনলোড করুন"
          sticky-label="📄 ফ্রি রুটিন PDF নিন"
          file-name="primary-headmaster-routine.pdf"
        />
      </div>
    </section>

    <!-- ══ FINAL CTA (dark brand band) ══ -->
    <section class="pl-cta" id="pl-enroll" ref="enrollRef">
      <div class="pl-cta-glow"></div>
      <div class="pl-container pl-cta-inner">
        <div class="pl-cta-copy">
          <span class="pl-cta-badge">{{ meta.category }} প্রস্তুতি</span>
          <h2 class="pl-cta-title">{{ courseDetails.cta_title || 'আজই আপনার প্রস্তুতি শুরু করুন' }}</h2>
          <p class="pl-cta-text">{{ courseDetails.cta_text }}</p>
          <ul class="pl-cta-feats">
            <li><span class="pl-tick">✓</span> ব্যাখ্যাসহ প্রতিটি প্রশ্নের সমাধান</li>
            <li><span class="pl-tick">✓</span> ফিক্সড রুটিন ও লাইভ মেরিট পজিশন</li>
            <li><span class="pl-tick">✓</span> সম্পূর্ণ সিলেবাস কভার</li>
          </ul>
        </div>
        <div class="pl-cta-box">
          <span class="pl-cta-box-ic"><i class="fas fa-graduation-cap"></i></span>
          <h3 class="pl-cta-box-title">এখনই এনরোল করুন</h3>
          <p class="pl-cta-box-sub">বিসিএস প্রস্তুতি অ্যাপ বা ওয়েবসাইট থেকে যুক্ত হোন।</p>
          <a href="https://www.bcsprostuti.com" target="_blank" class="pl-btn pl-btn-glow pl-w-full">
            ওয়েবসাইটে যুক্ত হোন
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti"
             target="_blank" class="pl-btn pl-btn-ghost pl-w-full">
            <i class="fab fa-google-play"></i> অ্যাপ ডাউনলোড করুন
          </a>
          <div class="pl-cta-trust">
            <span class="pl-cta-trust-stars">★★★★★</span>
            <span>৯ লক্ষ+ শিক্ষার্থীর আস্থা</span>
          </div>
          <a href="tel:+8801894442944" class="pl-call">
            <span class="pl-call-ic"><i class="fas fa-phone-alt"></i></span>
            <span><small>যেকোনো জিজ্ঞাসায় কল করুন</small><strong>০১৮৯৪-৪৪২৯৪৪</strong></span>
          </a>
        </div>
      </div>
    </section>
  </div>

  <!-- ════════════════════════════════════════════════════════════
       DEFAULT LAYOUT  (existing prose-based course details)
       ════════════════════════════════════════════════════════════ -->
  <template v-else>
    <div class="cd">
      <div class="cd-container">
        <a class="cd-back" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          সকল কোর্স
        </a>

        <header class="cd-head">
          <span class="cd-eyebrow">{{ meta.category }} প্রস্তুতি</span>
          <h1 class="cd-title">{{ courseDetails ? courseDetails.title : '' }}</h1>
          <p class="cd-lead" v-if="courseDetails && courseDetails.short_description">{{ courseDetails.short_description }}</p>
        </header>

        <div class="cd-banner">
          <img :src="courseDetails.image" :alt="courseDetails.title" loading="lazy" />
        </div>

        <div v-if="keyInfo.length" class="cd-stats">
          <div class="cd-stat" v-for="(k, i) in keyInfo" :key="i">
            <span class="cd-stat-ic"><i class="fas" :class="k.icon"></i></span>
            <span class="cd-stat-text">
              <span class="cd-stat-val">{{ k.value }}</span>
              <span class="cd-stat-lbl">{{ k.label }}</span>
            </span>
          </div>
        </div>

        <div class="cd-grid">
          <article class="cd-article">
            <div class="cd-card">
              <h2 class="cd-section-title">কোর্স পরিচিতি</h2>
              <div class="cd-prose" v-html="proseHtml"></div>
            </div>
          </article>

          <aside class="cd-aside">
            <div class="cd-cta">
              <span class="cd-cta-badge">{{ meta.category }} প্রস্তুতি</span>
              <h3 class="cd-cta-title">এখনই প্রস্তুতি শুরু করুন</h3>
              <p class="cd-cta-sub">বিসিএস প্রস্তুতি অ্যাপ বা ওয়েবসাইট থেকে যুক্ত হোন।</p>

              <ul class="cd-feats">
                <li><span class="cd-tick">✓</span> ক্যাডার-এনালাইজড পূর্ণ সিলেবাস</li>
                <li><span class="cd-tick">✓</span> ফিক্সড রুটিন ও নিয়মিত পরীক্ষা</li>
                <li><span class="cd-tick">✓</span> ব্যাখ্যাসহ প্রশ্ন সমাধান</li>
              </ul>

              <a href="https://www.bcsprostuti.com" target="_blank" class="cd-btn cd-btn-primary">
                ওয়েবসাইটে যুক্ত হোন
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti"
                 target="_blank" class="cd-btn cd-btn-ghost">
                <i class="fab fa-google-play"></i> অ্যাপ ডাউনলোড করুন
              </a>

              <a href="tel:+8801894442944" class="cd-call">
                <span class="cd-call-ic"><i class="fas fa-phone-alt"></i></span>
                <span>
                  <small>যেকোনো জিজ্ঞাসায় কল করুন</small>
                  <strong>০১৮৯৪৪৪২৯৪৪</strong>
                </span>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </template>

  <WelcomeFooter />
</template>
<script>
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import GuidelineOptin from "@/components/WelcomePages/GuidelineOptin.vue";
import {computed, onMounted, ref} from 'vue';
import {getCourseDetails} from "@/config/utils";
import {useRoute, useRouter} from "vue-router";

export default {
  components: {
    WelcomeFooter,
    WelcomeHeader,
    GuidelineOptin
  },
  setup() {
    const courseDetails = ref({})
    const route = useRoute();
    const router = useRouter();
    const enrollRef = ref(null);

    onMounted(() => {
      courseDetails.value = getCourseDetails(route.params.title);
      window.scrollTo(0, 0);
    });

    const isPremium = computed(() => courseDetails.value && courseDetails.value.layout === 'premium');

    const toBengali = (n) => {
      const bn = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
      return String(n).split('').map((c) => bn[+c] ?? c).join('');
    };

    // Subject → display name + colour key (colour comes from `.rg-{key}` CSS)
    const groupMeta = {
      bangla:        { name: "বাংলা" },
      english:       { name: "ইংরেজি" },
      gk:            { name: "সাধারণ জ্ঞান" },
      math:          { name: "গণিত" },
      science:       { name: "সাধারণ বিজ্ঞান" },
      bangladesh:    { name: "বাংলাদেশ" },
      international: { name: "আন্তর্জাতিক" },
      geography:     { name: "ভূগোল" },
      ict:           { name: "আইসিটি" },
      mental:        { name: "মানসিক দক্ষতা" },
      ethics:        { name: "নৈতিকতা ও সুশাসন" },
      banking:       { name: "ব্যাংকিং নলেজ" },
      analytical:    { name: "অ্যানালিটিক্যাল স্কিল" },
      exam:          { name: "সাপ্তাহিক ফাইনাল" },
    };

    // Canonical ordering for the colour legend (only groups present are shown)
    const GROUP_ORDER = ['bangla','english','science','math','bangladesh','international','geography','ict','mental','analytical','banking','ethics','gk','exam'];
    const legendGroups = computed(() => {
      const rows = (courseDetails.value && courseDetails.value.routine) || [];
      const present = new Set(rows.map((r) => r.group));
      return GROUP_ORDER.filter((k) => present.has(k) && groupMeta[k]);
    });

    const phaseLabels = {
      1: "ধাপ ০১ — টপিকভিত্তিক পূর্ণাঙ্গ পরীক্ষা",
      2: "ধাপ ০২ — ফাইনাল রিভিশন সাইকেল",
    };

    // Group the flat routine array into ordered phase blocks for the table
    const routinePhases = computed(() => {
      const rows = (courseDetails.value && courseDetails.value.routine) || [];
      const blocks = [];
      rows.forEach((r) => {
        let b = blocks.find((x) => x.phase === r.phase);
        if (!b) { b = { phase: r.phase, label: phaseLabels[r.phase] || '', rows: [] }; blocks.push(b); }
        b.rows.push(r);
      });
      return blocks;
    });

    // ── Month-wise view for long routines (e.g. the 6-month course's 144 exams) ──
    const isMonthRoutine = computed(
      () => (courseDetails.value && courseDetails.value.routine_view) === 'month'
    );
    const MONTH_ORDER = ['জানুয়ারি','ফেব্রুয়ারি','মার্চ','এপ্রিল','মে','জুন','জুলাই','আগস্ট','সেপ্টেম্বর','অক্টোবর','নভেম্বর','ডিসেম্বর'];
    const monthOf = (date) => MONTH_ORDER.find((m) => (date || '').includes(m)) || 'অন্যান্য';
    const routineMonths = computed(() => {
      const rows = (courseDetails.value && courseDetails.value.routine) || [];
      const map = new Map();
      rows.forEach((r, i) => {
        const key = monthOf(r.date);
        if (!map.has(key)) map.set(key, { key, name: key, rows: [] });
        map.get(key).rows.push({ ...r, no: toBengali(i + 1) });
      });
      // Order by first appearance in the routine (chronological, works across year boundaries)
      const ordered = Array.from(map.values()).filter((m) => m.key !== 'অন্যান্য');
      if (map.has('অন্যান্য')) ordered.push(map.get('অন্যান্য'));
      return ordered;
    });
    const activeMonth = ref('');
    const activeMonthKey = computed(() => {
      const months = routineMonths.value;
      if (activeMonth.value && months.some((m) => m.key === activeMonth.value)) return activeMonth.value;
      // default to the current calendar month if it's part of the routine
      const currentMonth = MONTH_ORDER[new Date().getMonth()];
      if (months.some((m) => m.key === currentMonth)) return currentMonth;
      return months.length ? months[0].key : '';
    });
    const setMonth = (key) => { activeMonth.value = key; };

    const scrollToEnroll = () => {
      const el = enrollRef.value;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const routineRef = ref(null);
    const scrollToRoutine = () => {
      const el = routineRef.value;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const meta = computed(() => {
      const t = (courseDetails.value && courseDetails.value.title) || '';
      if (t.includes('মডেল টেস্ট')) return { category: 'মডেল টেস্ট', cls: 'cat-test' };
      if (t.includes('সল্যুশন') || t.includes('সমাধান')) return { category: 'জব সল্যুশন', cls: 'cat-solution' };
      if (t.includes('ব্যাংক')) return { category: 'ব্যাংক', cls: 'cat-bank' };
      if (t.includes('প্রাইমারি')) return { category: 'প্রাইমারি', cls: 'cat-primary' };
      if (t.includes('বিসিএস')) return { category: 'বিসিএস', cls: 'cat-bcs' };
      return { category: 'কোর্স', cls: 'cat-default' };
    });

    const iconFor = (label) => {
      if (label.includes('সাপ্তাহিক') || label.includes('ফাইনাল')) return 'fa-trophy';
      if (label.includes('মোট') && label.includes('পরীক্ষা')) return 'fa-clipboard-list';
      if (label.includes('পরীক্ষা')) return 'fa-calendar-week';
      if (label.includes('সময়') || label.includes('মেয়াদ') || label.includes('দিন')) return 'fa-calendar-alt';
      if (label.includes('রুটিন')) return 'fa-route';
      return 'fa-check-circle';
    };

    // Parse the long description ONCE into short "label: value" highlight facts
    // (key-info cards) and the remaining prose. A line only counts as a fact when
    // it's a bullet with a SHORT label AND value — sentences, paragraphs and lines
    // containing links always stay in the prose (so no content is ever lost).
    const BULLET = /^(?:✅|🔵|✔️|✔|☑️|◾|▪️|•)\s*/u;
    const parsed = computed(() => {
      const ld = (courseDetails.value && courseDetails.value.long_description) || '';
      const lines = ld.split('\n');
      const stats = [];
      const consumed = new Set();
      lines.forEach((line, idx) => {
        const raw = line.trim();
        if (!BULLET.test(raw)) return;
        const rest = raw.replace(BULLET, '');
        if (/<a\b|href=|https?:\/\//i.test(rest)) return; // linked bullet → keep in prose
        const sep = rest.search(/ঃ|:/);
        if (sep === -1) return;                           // no label:value → it's a sentence
        const label = rest.slice(0, sep).trim().replace(/।$/, '').trim();
        const value = rest.slice(sep + 1).trim().replace(/।$/, '').trim();
        if (!label || !value) return;
        if (label.length > 22 || value.length > 28) return; // too long → prose, not a fact
        stats.push({ label, value, icon: iconFor(label) });
        consumed.add(idx);
      });
      const prose = lines
        .filter((_, i) => !consumed.has(i))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      return { stats, prose };
    });

    const keyInfo = computed(() => parsed.value.stats);
    const proseHtml = computed(() => parsed.value.prose);

    const goBack = () => {
      router.push({ name: 'CourseList' });
    };

    // Split a syllabus string (topics separated by "●") into clean list items
    const syllabusItems = (text) =>
      String(text || '')
        .split('●')
        .map((s) => s.trim())
        .filter(Boolean);

    return {
      syllabusItems,
      courseDetails,
      isPremium,
      groupMeta,
      legendGroups,
      routinePhases,
      isMonthRoutine,
      routineMonths,
      activeMonthKey,
      setMonth,
      scrollToEnroll,
      enrollRef,
      scrollToRoutine,
      routineRef,
      meta,
      keyInfo,
      proseHtml,
      goBack,
    };
  },
  metaInfo() {
    return {
      title: this.courseDetails.meta_title || this.courseDetails.title,
      meta: [
        { name: 'description', content: this.courseDetails.long_description },
        { property: 'og:title', content: this.courseDetails.meta_title || this.courseDetails.title },
        { property: 'og:description', content: this.courseDetails.long_description },
        { property: 'og:image', content: this.courseDetails.image },
        { property: 'og:url', content: `www.bcsprostuti.com/course-details/${this.courseDetails.url_text}` }
      ]
    }
  }
};
</script>

<style scoped>
/* ╔══════════════════════════════════════════════════════════════╗
   ║  PREMIUM LANDING LAYOUT                                       ║
   ╚══════════════════════════════════════════════════════════════╝ */
.pl {
  /* per-subject accent tokens — drive the colour-coded routine + legend */
  --rg-bangla: #e11d48;  --rg-bangla-bg: #fff1f3;  --rg-bangla-soft: #ffe4e8;
  --rg-english: #2563eb; --rg-english-bg: #eef4ff; --rg-english-soft: #dbe8ff;
  --rg-gk: #c2740a;      --rg-gk-bg: #fff5e6;      --rg-gk-soft: #fbe7c6;
  --rg-math: #047857;    --rg-math-bg: #e9f8f0;    --rg-math-soft: #cdeede;
  --rg-science: #7c3aed; --rg-science-bg: #f4edff; --rg-science-soft: #e7d9ff;
  --rg-bangladesh: #0891b2;    --rg-bangladesh-bg: #e6f6fb;    --rg-bangladesh-soft: #bfe6f2;
  --rg-international: #b45309;  --rg-international-bg: #fff6e9;  --rg-international-soft: #f6e1c1;
  --rg-geography: #0369a1;      --rg-geography-bg: #e9f4fb;      --rg-geography-soft: #c7e3f4;
  --rg-ict: #4f46e5;            --rg-ict-bg: #eef0fe;            --rg-ict-soft: #d8dcfb;
  --rg-mental: #be185d;         --rg-mental-bg: #fdeef5;         --rg-mental-soft: #f8d2e3;
  --rg-ethics: #15803d;         --rg-ethics-bg: #ecfdf3;          --rg-ethics-soft: #cdeed7;
  --rg-banking: #0f766e;        --rg-banking-bg: #e7f6f3;         --rg-banking-soft: #bfe6df;
  --rg-analytical: #a21caf;     --rg-analytical-bg: #fbeafc;      --rg-analytical-soft: #f0c4ee;
  --rg-exam: #0b1437;          --rg-exam-bg: #eef1f8;          --rg-exam-soft: #d6deee;
  font-family: 'Noto Sans Bengali', sans-serif;
  background: #f6f8fc;
}
.pl-container { width: 100%; max-width: 1180px; margin: 0 auto; padding: 0 24px; }
@media (max-width: 575px) { .pl-container { padding: 0 16px; } }

/* shared section heading */
.pl-head { text-align: center; max-width: 680px; margin: 0 auto 40px; }
.pl-eyebrow {
  display: inline-block; font-size: 0.74rem; font-weight: 800;
  letter-spacing: 0.14em; text-transform: uppercase; color: #0095FF; margin-bottom: 10px;
}
.pl-h2 {
  font-family: 'Noto Sans Bengali', sans-serif;
  font-size: clamp(1.5rem, 3.4vw, 2.3rem); font-weight: 700; line-height: 1.5;
  color: #131638; margin: 0;
}
.pl-note { font-size: 0.95rem; color: #64748b; margin: 12px 0 0; }

/* shared buttons */
.pl-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.98rem; font-weight: 700;
  padding: 14px 26px; border-radius: 13px; cursor: pointer; text-decoration: none;
  transition: transform .2s, box-shadow .2s, background .2s, color .2s; white-space: nowrap;
}
.pl-btn-glow {
  background: linear-gradient(100deg, #0095FF, #0066CC); color: #fff;
  box-shadow: 0 12px 26px -10px rgba(0,149,255,0.75);
}
.pl-btn-glow:hover { transform: translateY(-3px); box-shadow: 0 18px 34px -10px rgba(0,149,255,0.85); color: #fff; }
.pl-btn-line { background: rgba(255,255,255,0.08); color: #eaf2ff; border: 1px solid rgba(255,255,255,0.28); }
.pl-btn-line:hover { background: rgba(255,255,255,0.16); color: #fff; }
.pl-btn-soft {
  background: #ffffff; color: #0b1b3a; border: 1px solid #ffffff;
  box-shadow: 0 12px 26px -14px rgba(0,0,0,0.6);
}
.pl-btn-soft i { color: #0095FF; }
.pl-btn-soft:hover { transform: translateY(-3px); background: #eef5ff; color: #0066CC; box-shadow: 0 18px 32px -14px rgba(0,0,0,0.65); }
.pl-btn-ghost { background: rgba(255,255,255,0.08); color: #fff; border: 1px solid rgba(255,255,255,0.18); }
.pl-btn-ghost:hover { background: rgba(255,255,255,0.14); color: #fff; }
.pl-w-full { width: 100%; margin-bottom: 12px; }

/* ══ HERO ══ */
.pl-hero {
  position: relative; overflow: hidden;
  background: radial-gradient(120% 120% at 100% 0%, #1b1e52 0%, #14163a 45%, #0d0f2b 100%);
  padding-bottom: 48px;
}
.pl-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}
.pl-hero-badge-row { display: flex; justify-content: center; }
.pl-banner-wrap {
  position: relative; z-index: 2; margin: 6px auto 26px; max-width: 600px;
  border-radius: 18px; overflow: hidden;
  border: 1px solid rgba(255,255,255,0.10);
  box-shadow: 0 24px 60px -22px rgba(0,0,0,0.6);
}
.pl-banner {
  display: block; width: 100%; height: auto;
}
.pl-hero-glow {
  position: absolute; top: -120px; right: -90px; width: 480px; height: 480px; border-radius: 50%;
  background: radial-gradient(circle, rgba(0,149,255,0.40) 0%, rgba(0,149,255,0) 65%); pointer-events: none;
}
.pl-hero-inner {
  position: relative; z-index: 2; max-width: 980px; margin: 0 auto;
  padding-top: 58px; padding-bottom: 18px;
  animation: plUp .7s cubic-bezier(.2,.7,.2,1) both;
}
.pl-back {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.88rem; font-weight: 600;
  color: #c2cdf2; cursor: pointer; margin-bottom: 22px; transition: color .2s, gap .2s;
}
.pl-back:hover { color: #fff; gap: 11px; }
.pl-hero-badge {
  display: inline-flex; align-items: center; gap: 9px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.8rem; font-weight: 700; letter-spacing: 0.02em;
  color: #bfe3ff; background: rgba(0,149,255,0.14); border: 1px solid rgba(0,149,255,0.36);
  padding: 7px 17px; border-radius: 999px; margin-bottom: 22px;
}
.pl-hero-pulse {
  width: 8px; height: 8px; border-radius: 50%; background: #38B6FF;
  box-shadow: 0 0 0 0 rgba(56,182,255,0.6); animation: plPulse 2s infinite;
}
@keyframes plPulse {
  0% { box-shadow: 0 0 0 0 rgba(56,182,255,0.55); }
  70% { box-shadow: 0 0 0 10px rgba(56,182,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(56,182,255,0); }
}
.pl-hero-title {
  font-family: 'Noto Sans Bengali', sans-serif;
  font-size: clamp(1.7rem, 4vw, 2.55rem); font-weight: 700; line-height: 1.34;
  color: #fff; margin: 4px 0 14px; letter-spacing: -0.01em; text-align: center;
  text-shadow: 0 2px 22px rgba(0,0,0,0.35);
}
.pl-hero-sub { font-size: 0.98rem; line-height: 1.85; color: #b9c1e4; margin: 0 auto 24px; max-width: 660px; text-align: center; }
.pl-hero-cta { display: flex; flex-wrap: wrap; justify-content: center; gap: 14px; }

/* ══ STATS strip — white panel sitting at the bottom of the dark hero ══ */
.pl-stats {
  position: relative; z-index: 3;
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  margin: 36px 0 0;
}
@media (max-width: 760px) { .pl-stats { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 420px) { .pl-stats { grid-template-columns: 1fr; } }
.pl-stat {
  display: flex; align-items: center; gap: 13px;
  background: #fff; border: 1px solid #e7edf6; border-radius: 16px; padding: 16px 18px;
  box-shadow: 0 22px 50px -28px rgba(16,30,80,0.5);
}
.pl-stat-ic {
  flex: none; width: 46px; height: 46px; border-radius: 12px;
  background: linear-gradient(140deg, #0095FF, #0066CC); color: #fff;
  display: grid; place-items: center; font-size: 1.05rem;
}
.pl-stat-text { display: flex; flex-direction: column; min-width: 0; }
.pl-stat-val { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.05rem; font-weight: 700; color: #131638; line-height: 1.42; }
.pl-stat-lbl { font-size: 0.78rem; color: #6b7690; margin-top: 2px; }

/* ══ FEATURES (light blue) ══ */
.pl-features {
  background: radial-gradient(90% 70% at 50% 0%, #eaf3fd 0%, #f1f6fc 60%), #f1f6fc;
  padding: 64px 0;
}
.pl-feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
@media (max-width: 900px) { .pl-feat-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .pl-feat-grid { grid-template-columns: 1fr; } }
.pl-feat-card {
  position: relative; background: #fff; border: 1px solid #e7edf6; border-radius: 18px;
  padding: 26px 24px 24px; overflow: hidden;
  box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  transition: transform .28s cubic-bezier(.2,.7,.2,1), box-shadow .28s, border-color .28s;
  animation: plUp .6s cubic-bezier(.2,.7,.2,1) both;
}
.pl-feat-card::before {
  content: ""; position: absolute; top: 0; left: 0; width: 100%; height: 4px;
  background: var(--fc, #0095FF);
}
.pl-feat-card:hover { transform: translateY(-6px); box-shadow: 0 24px 46px -22px rgba(16,30,80,0.32); }
.pl-feat-ic {
  display: grid; place-items: center; width: 52px; height: 52px; border-radius: 14px;
  font-size: 1.25rem; margin-bottom: 16px;
  color: var(--fc, #0095FF); background: var(--fc-bg, #e7f1ff);
}
.pl-feat-title { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.12rem; font-weight: 700; color: #131638; margin: 0 0 8px; line-height: 1.5; }
.pl-feat-desc { font-size: 0.9rem; line-height: 1.7; color: #5b6678; margin: 0; }
/* feature accent palette (cycled by index) */
.fc-0 { --fc: #0095FF; --fc-bg: #e6f2ff; }
.fc-1 { --fc: #7c3aed; --fc-bg: #f3ecfe; }
.fc-2 { --fc: #c2740a; --fc-bg: #fff3e0; }
.fc-3 { --fc: #047857; --fc-bg: #e7f6ef; }
.fc-4 { --fc: #e11d48; --fc-bg: #ffe9ed; }
.fc-5 { --fc: #0d9488; --fc-bg: #e2f6f3; }

/* ══ WHY (dark indigo band) ══ */
.pl-why {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #201b5a 0%, #15184a 55%, #0f1238 100%);
  padding: 46px 0;
}
.pl-why-orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(6px); }
.pl-why-orb-a { top: -90px; left: -70px; width: 260px; height: 260px; background: radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 65%); }
.pl-why-orb-b { bottom: -120px; right: -80px; width: 300px; height: 300px; background: radial-gradient(circle, rgba(0,149,255,0.28) 0%, transparent 65%); }
.pl-why-inner { position: relative; z-index: 2; max-width: 720px; text-align: center; margin: 0 auto; }
.pl-why-mark {
  display: inline-grid; place-items: center; width: 46px; height: 46px; border-radius: 13px;
  background: rgba(56,182,255,0.16); border: 1px solid rgba(56,182,255,0.34);
  color: #6cc6ff; font-size: 1.1rem; margin-bottom: 14px;
}
.pl-why-title { font-family: 'Noto Sans Bengali', sans-serif; font-size: clamp(1.3rem, 2.6vw, 1.7rem); font-weight: 700; color: #fff; margin: 0 0 12px; line-height: 1.4; }
.pl-why-text { font-size: 0.96rem; line-height: 1.78; color: #c3cbeb; margin: 0; }

/* ══ ROUTINE (light, colour-coded) ══ */
.pl-routine { background: #f6f8fc; padding: 66px 0; }
.pl-legend { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin: 0 0 26px; }
.pl-legend-item {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.8rem; font-weight: 600;
  padding: 6px 13px; border-radius: 999px;
  color: var(--rg); background: var(--rg-bg); border: 1px solid var(--rg-soft);
}
.pl-legend-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--rg); }

.pl-routine-card {
  background: #fff; border: 1px solid #e7edf6; border-radius: 22px;
  padding: 12px 12px 18px; box-shadow: 0 24px 60px -34px rgba(16,30,80,0.4); overflow: hidden;
}
@media (max-width: 575px) { .pl-routine-card { border-radius: 18px; padding: 6px 6px 12px; } }
.pl-phase { padding: 14px 12px 4px; }
.pl-phase-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.pl-phase-tag {
  display: inline-flex; align-items: center;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.92rem; font-weight: 700; color: #131638;
  padding-left: 14px; position: relative;
}
.pl-phase-tag::before { content: ""; position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 5px; height: 22px; border-radius: 3px; background: linear-gradient(#0095FF, #0066CC); }
.pl-phase-count { font-size: 0.78rem; font-weight: 600; color: #6b7690; background: #eef2f9; padding: 4px 12px; border-radius: 999px; }

.pl-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 14px; }
.pl-table { width: 100%; border-collapse: separate; border-spacing: 0 8px; min-width: 520px; }
.pl-table thead th {
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.76rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em; color: #8590ab; text-align: left;
  padding: 4px 16px 10px;
}
.pl-table thead .c-no { width: 56px; text-align: left; }
.pl-table thead .c-date { width: 110px; }
.pl-table thead .c-day { width: 130px; }

.pl-table tbody tr {
  background: var(--rg-bg, #f7f9fc);
  animation: plUp .5s cubic-bezier(.2,.7,.2,1) both;
  transition: transform .18s, box-shadow .22s;
}
.pl-table tbody tr:hover { transform: translateX(3px); box-shadow: 0 12px 26px -16px rgba(16,30,80,0.4); }
.pl-table tbody td {
  padding: 13px 16px; font-size: 0.92rem; color: #3c4456; vertical-align: middle;
  text-align: left;
  border-top: 1px solid color-mix(in srgb, var(--rg) 16%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--rg) 16%, transparent);
}
.pl-table tbody td.c-no {
  border-left: 4px solid var(--rg, #0095FF);
  border-top-left-radius: 13px; border-bottom-left-radius: 13px; text-align: left; width: 56px;
}
.pl-table tbody td.c-sub { border-top-right-radius: 13px; border-bottom-right-radius: 13px; }
.pl-rno {
  display: inline-grid; place-items: center; min-width: 34px; height: 34px; padding: 0 6px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.95rem; font-weight: 800;
  border-radius: 10px; color: var(--rg); background: #fff; border: 1.5px solid var(--rg-soft);
}
.c-date { font-weight: 600; color: #2a3142; white-space: nowrap; }
.c-day { color: #6b7690; }
.pl-sub { display: inline-flex; align-items: center; gap: 11px; flex-wrap: wrap; }
.pl-sub-text { font-family: 'Noto Sans Bengali', sans-serif; font-weight: 600; color: #1f2540; }
.pl-sub-chip {
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.7rem; font-weight: 700;
  color: var(--rg); background: #fff; border: 1px solid var(--rg-soft);
  padding: 3px 11px; border-radius: 999px; white-space: nowrap; line-height: 1.7;
}

/* ── Per-row syllabus (always visible) ── */
.pl-syl { margin-top: 10px; }
.pl-syl-ico {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.66rem; font-weight: 600;
  color: var(--rg); background: #fff; border: 1px solid var(--rg-soft);
  padding: 2px 11px; border-radius: 999px; line-height: 1.7; opacity: 0.92;
}
.pl-syl-list {
  margin: 8px 0 2px; padding: 12px 14px 12px 30px; list-style: disc;
  background: var(--rg-soft, #e6ecf5); border: 1px solid var(--rg-soft, #e6ecf5);
  border-left: 2px solid var(--rg-soft, #cbb26a); border-radius: 10px;
}
.pl-syl-list li {
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.82rem;
  line-height: 1.75; color: #334155; margin: 0 0 6px; padding-left: 2px;
}
.pl-syl-list li::marker { color: var(--rg, #64748b); }
.pl-syl-list li:last-child { margin-bottom: 0; }

/* subject colour tokens applied to legend chips + table rows */
.rg-bangla  { --rg: var(--rg-bangla);  --rg-bg: var(--rg-bangla-bg);  --rg-soft: var(--rg-bangla-soft); }
.rg-english { --rg: var(--rg-english); --rg-bg: var(--rg-english-bg); --rg-soft: var(--rg-english-soft); }
.rg-gk      { --rg: var(--rg-gk);      --rg-bg: var(--rg-gk-bg);      --rg-soft: var(--rg-gk-soft); }
.rg-math    { --rg: var(--rg-math);    --rg-bg: var(--rg-math-bg);    --rg-soft: var(--rg-math-soft); }
.rg-science { --rg: var(--rg-science); --rg-bg: var(--rg-science-bg); --rg-soft: var(--rg-science-soft); }
.rg-bangladesh    { --rg: var(--rg-bangladesh);    --rg-bg: var(--rg-bangladesh-bg);    --rg-soft: var(--rg-bangladesh-soft); }
.rg-international  { --rg: var(--rg-international);  --rg-bg: var(--rg-international-bg);  --rg-soft: var(--rg-international-soft); }
.rg-geography      { --rg: var(--rg-geography);      --rg-bg: var(--rg-geography-bg);      --rg-soft: var(--rg-geography-soft); }
.rg-ict            { --rg: var(--rg-ict);            --rg-bg: var(--rg-ict-bg);            --rg-soft: var(--rg-ict-soft); }
.rg-mental         { --rg: var(--rg-mental);         --rg-bg: var(--rg-mental-bg);         --rg-soft: var(--rg-mental-soft); }
.rg-ethics         { --rg: var(--rg-ethics);         --rg-bg: var(--rg-ethics-bg);         --rg-soft: var(--rg-ethics-soft); }
.rg-banking        { --rg: var(--rg-banking);        --rg-bg: var(--rg-banking-bg);        --rg-soft: var(--rg-banking-soft); }
.rg-analytical     { --rg: var(--rg-analytical);     --rg-bg: var(--rg-analytical-bg);     --rg-soft: var(--rg-analytical-soft); }
.rg-exam           { --rg: var(--rg-exam);           --rg-bg: var(--rg-exam-bg);           --rg-soft: var(--rg-exam-soft); }
/* Weekly-final rows read as a milestone, not just another subject */
.pl-table tbody tr.rg-exam { background: linear-gradient(100deg, var(--rg-exam-bg), #fbfcfe); }
.pl-table tbody tr.rg-exam .pl-sub-text { font-weight: 800; }

/* ── Month switcher (long routines) ── */
.pl-month-switch { text-align: center; margin: 0 0 30px; }
.pl-month-label {
  display: block; font-family: 'Noto Sans Bengali', sans-serif;
  font-size: 0.9rem; font-weight: 800; color: #1b2150; margin: 0 auto 14px;
}
.pl-month-label i { color: #0095FF; margin-left: 4px; }
.pl-month-tabs {
  display: inline-flex; flex-wrap: wrap; gap: 8px; justify-content: center;
  margin: 0; padding: 10px; background: #e8eff9; border: 1.5px solid #cfdcee;
  border-radius: 20px; box-shadow: inset 0 1px 4px rgba(15,23,42,0.07);
}
.pl-month-tab {
  position: relative; display: inline-flex; align-items: center; gap: 9px;
  font-family: 'Noto Sans Bengali', sans-serif;
  font-size: 1.04rem; font-weight: 700; color: #3a4a66;
  background: #fff; border: 1.5px solid #d7e1ee;
  padding: 12px 22px; border-radius: 13px; cursor: pointer;
  transition: all .16s ease;
}
.pl-month-tab:hover { border-color: #8fc0ee; color: #0066CC; background: #f1f7ff; transform: translateY(-1px); }
.pl-month-tab.active {
  color: #fff; border-color: transparent;
  background: linear-gradient(120deg, #0095FF, #0066CC);
  box-shadow: 0 11px 22px -8px rgba(0,102,204,0.8);
  transform: translateY(-1px);
}
/* caret on the active tab pointing down to the routine table */
.pl-month-tab.active::after {
  content: ''; position: absolute; left: 50%; bottom: -20px; transform: translateX(-50%);
  border-left: 9px solid transparent; border-right: 9px solid transparent;
  border-top: 10px solid #0066CC;
}
.pl-month-tab-count {
  font-size: 0.74rem; font-weight: 800; line-height: 1;
  padding: 3px 9px; border-radius: 999px;
  color: #0066CC; background: #eaf3ff;
}
.pl-month-tab.active .pl-month-tab-count { color: #0066CC; background: #fff; }
@media (max-width: 640px) {
  .pl-month-tab { font-size: 0.96rem; padding: 10px 16px; }
  .pl-month-tab.active::after { display: none; }
}

/* ══ FINAL CTA (dark brand band) ══ */
.pl-cta {
  position: relative; overflow: hidden;
  background: radial-gradient(110% 120% at 0% 0%, #1b1e52 0%, #12152f 55%, #0c0e26 100%);
  padding: 70px 0;
}
.pl-cta-glow {
  position: absolute; top: -100px; left: 50%; transform: translateX(-50%);
  width: 620px; height: 360px; border-radius: 50%;
  background: radial-gradient(circle, rgba(0,149,255,0.28) 0%, rgba(0,149,255,0) 65%); pointer-events: none;
}
.pl-cta-inner {
  position: relative; z-index: 2;
  display: grid; grid-template-columns: 1.2fr 0.9fr; gap: 36px; align-items: center;
}
@media (max-width: 880px) { .pl-cta-inner { grid-template-columns: 1fr; gap: 26px; } }
.pl-cta-badge {
  display: inline-block; font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.74rem; font-weight: 700;
  color: #bfe3ff; background: rgba(0,149,255,0.14); border: 1px solid rgba(0,149,255,0.32);
  padding: 5px 14px; border-radius: 999px; margin-bottom: 16px;
}
.pl-cta-title { font-family: 'Noto Sans Bengali', sans-serif; font-size: clamp(1.5rem, 3.2vw, 2.2rem); font-weight: 700; color: #fff; margin: 0 0 14px; line-height: 1.45; }
.pl-cta-text { font-size: 1rem; line-height: 1.9; color: #b6bee0; margin: 0 0 22px; }
.pl-cta-feats { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.pl-cta-feats li { display: flex; align-items: center; gap: 11px; font-size: 0.94rem; color: #d6dcf3; }
.pl-tick { flex: none; width: 22px; height: 22px; border-radius: 50%; background: rgba(56,182,255,0.18); color: #38B6FF; display: grid; place-items: center; font-size: 0.72rem; font-weight: 800; }

.pl-cta-box {
  position: relative;
  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.035));
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 22px; padding: 30px 26px 26px; backdrop-filter: blur(8px);
  box-shadow: 0 32px 72px -34px rgba(0,0,0,0.75);
}
.pl-cta-box::before {
  content: ''; position: absolute; inset: 0; border-radius: 22px; padding: 1px;
  background: linear-gradient(140deg, rgba(0,149,255,0.6), rgba(255,255,255,0) 48%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none;
}
.pl-cta-box > * { position: relative; z-index: 1; }
.pl-cta-box-ic {
  display: inline-grid; place-items: center; width: 56px; height: 56px; border-radius: 16px;
  background: linear-gradient(135deg, #0095FF, #0066CC); color: #fff; font-size: 1.45rem;
  margin-bottom: 16px; box-shadow: 0 16px 30px -12px rgba(0,149,255,0.75);
}
.pl-cta-box-title { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.34rem; font-weight: 700; color: #fff; margin: 0 0 6px; }
.pl-cta-box-sub { font-size: 0.88rem; color: #a8b0d4; margin: 0 0 20px; line-height: 1.6; }
.pl-cta-trust {
  display: flex; align-items: center; justify-content: center; gap: 9px;
  margin: 16px 0 2px; font-size: 0.82rem; font-weight: 600; color: #c7cef0;
}
.pl-cta-trust-stars { color: #ffc83d; letter-spacing: 1px; font-size: 0.86rem; }
.pl-call {
  display: flex; align-items: center; gap: 12px; margin-top: 8px; padding-top: 18px;
  border-top: 1px solid rgba(255,255,255,0.1); text-decoration: none; color: #fff;
}
.pl-call-ic { flex: none; width: 42px; height: 42px; border-radius: 12px; background: rgba(56,182,255,0.16); color: #38B6FF; display: grid; place-items: center; font-size: 0.98rem; }
.pl-call small { display: block; font-size: 0.72rem; color: #a8b0d4; }
.pl-call strong { display: block; font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.14rem; letter-spacing: 0.02em; }
.pl-call:hover .pl-call-ic { background: rgba(56,182,255,0.28); }

@keyframes plUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) {
  .pl-hero-inner, .pl-feat-card, .pl-table tbody tr { animation: none; }
}

/* ╔══════════════════════════════════════════════════════════════╗
   ║  DEFAULT LAYOUT (unchanged)                                   ║
   ╚══════════════════════════════════════════════════════════════╝ */
/* minimal · light */
.cd { background: #ffffff; padding: 34px 0 72px; text-align: left; }
.cd-container { width: 100%; max-width: 1160px; margin: 0 auto; padding: 0 24px; text-align: left; }
@media (max-width: 575px) { .cd-container { padding: 0 18px; } }

.cd-back {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.88rem; font-weight: 600; color: #6b7585;
  cursor: pointer; transition: color .2s, gap .2s;
}
.cd-back:hover { color: #0066CC; gap: 11px; }

.cd-head { max-width: 800px; margin: 22px 0 26px; text-align: left; }
.cd-eyebrow {
  display: block; text-align: left; font-family: 'Noto Sans Bengali', sans-serif;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #0095FF; margin-bottom: 12px;
}
.cd-title {
  font-family: 'Noto Sans Bengali', sans-serif;
  font-size: clamp(1.7rem, 3.6vw, 2.5rem); font-weight: 700; line-height: 1.28;
  color: #0f1222; margin: 0; text-align: left; letter-spacing: -0.015em;
}
.cd-lead {
  font-family: 'Noto Sans Bengali', sans-serif;
  font-size: 1.02rem; line-height: 1.75; color: #5a6473; margin: 14px 0 0; text-align: left; white-space: pre-line;
}

.cd-banner {
  aspect-ratio: 1080 / 566; border-radius: 18px; overflow: hidden;
  border: 1px solid #eaeef3; background: #f1f5f9; margin-bottom: 30px;
}
.cd-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }

.cd-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 16px; margin-bottom: 34px; }
.cd-stat {
  display: flex; align-items: center; gap: 13px; background: #fff; border: 1px solid #eaeef3; border-radius: 14px;
  padding: 15px 17px;
}
.cd-stat-ic { flex: none; width: 42px; height: 42px; border-radius: 11px; background: #eef6ff; color: #0095FF; display: grid; place-items: center; font-size: 1rem; }
.cd-stat-text { display: flex; flex-direction: column; min-width: 0; }
.cd-stat-val { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.1rem; font-weight: 700; color: #0f1222; line-height: 1.3; }
.cd-stat-lbl { font-size: 0.78rem; color: #8a93a3; margin-top: 2px; }

.cd-grid { display: grid; grid-template-columns: minmax(0,1fr) 330px; gap: 32px; align-items: start; }
@media (max-width: 991px) { .cd-grid { grid-template-columns: 1fr; } }

.cd-card { background: #fff; border: 1px solid #eaeef3; border-radius: 18px; padding: 32px 36px 36px; }
@media (max-width: 575px) { .cd-card { padding: 24px 20px 28px; } }
.cd-section-title {
  font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0f1222;
  margin: 0 0 20px; padding-bottom: 16px; text-align: left; position: relative; border-bottom: 1px solid #eef1f5;
}
.cd-section-title::after { content: ""; position: absolute; bottom: -1px; left: 0; width: 48px; height: 2px; background: #0095FF; }

.cd-prose { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1rem; line-height: 1.95; color: #45505f; text-align: left; white-space: pre-line; }
.cd-prose :deep(a) { color: #0066CC; font-weight: 600; text-decoration: none; border-bottom: 1px solid rgba(0,102,204,0.28); transition: color .2s, border-color .2s; }
.cd-prose :deep(a:hover) { color: #0095FF; border-color: #0095FF; }
.cd-prose :deep(p) { margin: 0 0 14px; }
.cd-prose :deep(strong) { color: #0f1222; }

.cd-aside { position: sticky; top: 90px; }
@media (max-width: 991px) { .cd-aside { position: static; } }
.cd-cta { background: #fff; border: 1px solid #eaeef3; border-radius: 18px; padding: 26px 24px; box-shadow: 0 18px 40px -26px rgba(15,30,80,0.18); }
.cd-cta-badge { display: inline-block; font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.72rem; font-weight: 600; color: #0066CC; background: #eef6ff; border: 1px solid #d8e9fc; padding: 4px 12px; border-radius: 999px; margin-bottom: 14px; }
.cd-cta-title { font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0f1222; margin: 0 0 6px; text-align: left; }
.cd-cta-sub { font-size: 0.88rem; color: #6b7585; margin: 0 0 20px; text-align: left; line-height: 1.6; }
.cd-feats { list-style: none; padding: 0; margin: 0 0 22px; }
.cd-feats li { display: flex; align-items: flex-start; gap: 10px; font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.875rem; color: #45505f; line-height: 1.5; padding: 6px 0; text-align: left; }
.cd-tick { flex: none; width: 20px; height: 20px; border-radius: 50%; background: #eafaf1; color: #0b7a43; display: grid; place-items: center; font-size: 0.7rem; font-weight: 800; margin-top: 1px; }
.cd-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; font-family: 'Noto Sans Bengali', sans-serif; font-size: 0.95rem; font-weight: 600; padding: 13px 18px; border-radius: 12px; cursor: pointer; text-decoration: none; transition: transform .2s, box-shadow .2s, background .2s, border-color .2s; margin-bottom: 12px; }
.cd-btn-primary { background: #0095FF; color: #fff; box-shadow: 0 10px 22px -10px rgba(0,149,255,0.6); }
.cd-btn-primary:hover { transform: translateY(-2px); background: #0086e8; color: #fff; }
.cd-btn-ghost { background: #f6f8fb; color: #0f1222; border: 1px solid #e6eaf0; }
.cd-btn-ghost:hover { background: #eef2f7; color: #0f1222; }
.cd-call { display: flex; align-items: center; gap: 12px; margin-top: 8px; padding-top: 18px; border-top: 1px solid #eef1f5; text-decoration: none; color: #0f1222; }
.cd-call-ic { flex: none; width: 40px; height: 40px; border-radius: 11px; background: #eef6ff; color: #0095FF; display: grid; place-items: center; font-size: 0.95rem; }
.cd-call small { display: block; font-size: 0.72rem; color: #8a93a3; }
.cd-call strong { display: block; font-family: 'Noto Sans Bengali', sans-serif; font-size: 1.1rem; letter-spacing: 0.02em; color: #0f1222; }
.cd-call:hover .cd-call-ic { background: #e0efff; }
</style>